import React, { useState, useEffect, useMemo } from 'react';
import { useLoading } from 'shared/contexts/Loading';
import masterApiCall from 'shared/services/MasterDataApi';

/**
 * A single card component to display Concept information.
 * @param {object} props
 * @param {object} props.concept - The concept object to display.
 */
const ConceptCard = ({ concept, navigateTo }) => (
    <div
        className="flex flex-col rounded-lg 
             border border-gray-300 dark:border-gray-700 
             bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 
             shadow-sm hover:shadow-md 
             transition-transform duration-300 ease-out hover:scale-[1.01]"
    >
        <div
            className="p-4 cursor-pointer"
            onClick={(event) => {
                navigateTo("conceptDetails", concept.code);
            }}
        >
            <h2 className="mb-2 text-lg font-bold text-gray-900 dark:text-gray-100 tracking-wide">
                {concept.name}
            </h2>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {concept.description || "No description provided."}
            </p>
        </div>
    </div>

);

/**
 * Main application component to list and filter concepts.
 */
export default function ConceptsContent({ navigateTo, displayToast }) {
    const { startLoading, stopLoading } = useLoading();

    const [allConcepts, setAllConcepts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch data on component mount
    useEffect(() => {
        const fetchConcepts = async () => {
            try {
                masterApiCall(
                    "/concepts",
                    null,
                    "GET",
                    null,
                    startLoading,
                    stopLoading,
                    null
                )
                    .then((data) => {
                        setAllConcepts(data);
                    })
                    .catch((error) => {
                        console.error("Error fetching study materials:", error);
                        setError(true);
                    });
            } catch (err) {
                setError(err.message || 'Failed to fetch concepts.');
            } finally {
                setLoading(false);
            }
        };

        fetchConcepts();
    }, []); // Empty dependency array means this runs once on mount

    // Memoized derivation of the concepts to display.
    // This recalculates only when `allConcepts` or `searchTerm` changes.
    const displayedConcepts = useMemo(() => {
        return (
            allConcepts
                // 1. Filter by isActive flag
                .filter((concept) => concept.isActive)
                // 2. Filter by search term (case-insensitive)
                .filter((concept) =>
                    concept.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                // 3. Sort by name (alphabetical)
                .sort((a, b) => a.name.localeCompare(b.name))
        );
    }, [allConcepts, searchTerm]);

    return (
        <div className="min-h-screen  p-2 sm:p-4">
            <div className="mx-auto max-w-8xl pl-4">
                <header className="mb-6 flex flex-col items-start justify-between sm:flex-row sm:items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Concept Library</h1>
                        <p className="mt-1 text-gray-600 dark:text-gray-400">
                            Browse and search your active concepts.
                        </p>
                    </div>
                    {/* Search Bar */}
                    <div className="mt-4 w-full sm:mt-0 sm:w-auto">
                        <input
                            type="text"
                            placeholder="What's on your mind"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full max-w-lg rounded-md border-gray-300 p-3 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:w-64 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                        />
                    </div>
                </header>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center p-12">
                        <div
                            className="h-8 w-8 animate-spin rounded-full border-4 border-gray-700 border-t-transparent"
                            role="status"
                        >
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="rounded-md bg-gray-200 p-4 text-center text-gray-800">
                        <strong>Error:</strong> {error}
                    </div>
                )}

                {/* Content */}
                {!loading && !error && (
                    <>
                        {/* Results Grid */}
                        {displayedConcepts.length > 0 ? (
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {displayedConcepts.map((concept) => (
                                    <ConceptCard key={concept.code} concept={concept} navigateTo={navigateTo} />
                                ))}
                            </div>
                        ) : (
                            // No Results State
                            <div className="rounded-md border border-gray-200 bg-white p-12 text-center text-gray-500 shadow-sm">
                                <h3 className="text-lg font-medium">No Concepts Found</h3>
                                <p className="mt-1">
                                    {searchTerm
                                        ? 'Your search returned no active concepts. Try a different keyword.'
                                        : 'There are no active concepts to display.'}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}